import moment from 'moment';
import { getRoot } from 'mobx-easy';
import { RoutesPlannerConfiguration, RoutesPlannerRun } from '@bringg-frontend/routes-planner';
import { Run, Task, User, VehicleType } from '@bringg/types';
import { getTasksWeight } from '@bringg-frontend/shared-tasks-weight';

import { PropsWithRoute, UserAssignmentModalParams } from 'bringg-web/features/runs-assignment/user-assignment/consts';
import RootStore from 'bringg-web/stores/root-store';
import { isClusterModeEnabled } from '../../routes-planner-data-creator/routes-planner-data-creator';

const getRunData = (runId: number, runTasks: Task[], isSavedRun: boolean, teamId: number, route?: RoutesPlannerRun) => {
	const extraData = {
		team_id: teamId,
		scheduled_start_time: moment.unix(route.depot_etl).toISOString(),
		scheduled_end_time: moment.unix(route.depot_back_eta).toISOString(),
		planned_transport_distance: route.transport_distance
	};
	const runData = isSavedRun
		? {
				...route,
				...extraData
		  }
		: {
				id: runId,
				route_name: route?.title,
				user_id: route.user_id,
				tasks: runTasks,
				task_ids: runTasks.map(task => task.id),
				...extraData
		  };
	return runData;
};

const getUserTypeWeightCapacity = (selectedTasks: Task[]): number => {
	const firstTask: Task = selectedTasks[0];
	if (firstTask.user_id) {
		const user = getRoot<RootStore>().data.driversStore.get(firstTask.user_id);
		return getRoot<RootStore>().data.userTypeStore.get(user.user_type_id)?.max_total_weight;
	}
};

const getWeight = async (
	selectedTasks: Task[],
	configuration: RoutesPlannerConfiguration,
	vehicleTypeByVehicleIds: { [key: string]: VehicleType },
	route?: RoutesPlannerRun
): Promise<{ maxWeight: number; weightCapacity: number }> => {
	const isClusterMode = await isClusterModeEnabled();
	const weightData = getTasksWeight(
		selectedTasks,
		configuration.ignoreQuantityInLoadCalculation,
		configuration.uniqueInventoryFields,
		configuration.enableVehicles,
		vehicleTypeByVehicleIds,
		getUserTypeWeightCapacity,
		isClusterMode,
		route as unknown as Run
	);

	return weightData;
};

const getUserAssignerModalParams = async ({
	runId,
	runTasks,
	drivers,
	isSavedRun,
	teamId,
	configuration,
	vehicleTypeByVehicleIds,
	route
}: {
	runId: number;
	runTasks: Task[];
	drivers: User[];
	isSavedRun: boolean;
	teamId: number;
	configuration: RoutesPlannerConfiguration;
	vehicleTypeByVehicleIds: { [key: string]: VehicleType };
	route?: RoutesPlannerRun;
}): Promise<UserAssignmentModalParams> => {
	const sourceRun = getRunData(runId, runTasks, isSavedRun, teamId, route);
	await getRoot<RootStore>().data.userTypeStore.fetchAll();
	const tasksWeight = await getWeight(runTasks, configuration, vehicleTypeByVehicleIds, route);

	const params = {
		source: {
			isAllRunWasSelected: isSavedRun,
			run: sourceRun,
			runTasks,
			selectedTasksWeight: tasksWeight
		} as PropsWithRoute,
		readyToExecute: runTasks[0].ready_to_execute,
		data: {
			tasksRunIds: [runId],
			drivers,
			doNotNotifyServerOnAssign: true,
			allowedActions: ['assign'],
			isSavedRun
		}
	};

	return params;
};

export const userAssignerModalOpener = { getUserAssignerModalParams };
